import {
  Container,
  ContainerGutter,
  ContainerPadding,
  CTA,
  ReferenceBadge,
  Text,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GolfFormLoader } from './golfFormLoader';
import ScrollHintArrow from '../ScrollHintArrow';

gsap.registerPlugin(ScrollTrigger);

const BackgroundGtiColor = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;

  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/black-background?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT0xMDgwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmRjYzM=');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin-top: -2px;

  overflow-x: hidden;
`;

const ScrollHintArrowLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
`;

const FormContainer = styled.div`
  --title-size: clamp(56px, 10vw, 190px);
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  line-height: 1;

  @media screen and (max-width: 1280px) {
    --title-size: clamp(52px, 9vw, 150px);
  }

  @media screen and (max-width: 1100px) {
    --title-size: clamp(46px, 8vw, 120px);
  }

  @media screen and (max-width: 960px) {
    --title-size: clamp(34px, 11vw, 96px);
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
`;

const FormText = styled.h2`
  margin: 0;
  color: #c90010;
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-stretch: condensed;
  letter-spacing: 0.01em;
  font-size: var(--title-size);
  position: relative;
  width: fit-content;
  max-width: 100%;
  white-space: nowrap;
  line-height: 0.92;

  @media screen and (max-width: 960px) {
    white-space: normal;
  }
`;

const DesktopTitleGroup = styled(TitleGroup)`
  box-sizing: border-box;
  width: 100%;
  padding-inline: clamp(24px, 4vw, 48px);

  @media screen and (max-width: 960px) {
    display: none;
  }
`;

const MobileTitleGroup = styled(TitleGroup)`
  display: none;

  @media screen and (max-width: 960px) {
    display: flex;
  }
`;

const MobileTitleLine = styled(FormText)`
  display: block;
  width: 100%;
  text-align: center;
`;

const InterroganteApertura = styled.span`
  position: relative;
  top: -0.17em;
`;

const FirstText = styled(FormText)`
  color: #ffffff;
  &::after {
    content: 'GT';
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const SecondText = styled(FormText)`
  &::before {
    content: 'TINES';
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const SecondTextWhite = styled(FormText)`
  color: #ffffff;
`;

const SecondTextJoined = styled(SecondText)`
  &::before {
    content: none;
  }
`;

const SecondLineWrapper = styled.div`
  display: inline-flex;
  align-items: flex-end;
  max-width: 100%;
`;
const SvgDecoration = styled.svg`
  position: absolute;
  height: calc(var(--title-size) * 1.1);
  width: auto;
  z-index: 2;
  pointer-events: none;
  transform: translate(50%, 5%);
`;

const DescriptionText = styled(Text)`
  font-weight: 700;
  font-style: Bold;
  leading-trim: NONE;
  line-height: 120%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;
`;

const SecondaryText = styled(Text)`
  font-weight: 300;
  font-style: Light;
  leading-trim: NONE;
  line-height: 120%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;
`;

const StyledCTA = styled(CTA)`
  background-color: #c90010;
  color: #ffffff;
  will-change: transform, background-color, color;
  transform-origin: center center;

  :hover,
  :active,
  :focus,
  :focus-visible {
    background: #8a010cff !important;
    color: #ffffff !important;
  }
`;

const StyledReferenceBadge = styled(ReferenceBadge)`
	vertical-align: top;
  pointer-events: none;
	&::before {
		content: none;
	}
`;

export const FormGti50 = (): React.ReactElement => {
  const [renderFormGolf, setRenderFormGolf] = React.useState<boolean>(false);
  const ctaRef = React.useRef<HTMLButtonElement | null>(null);
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) return;

    const ctaEl = ctaRef.current as unknown as HTMLElement | null;
    if (!ctaEl) return;

    // Timeline: rebote al crecer, pausa de 1s y rebote al volver
    const tl = gsap
      .timeline({ paused: true })
      .to(ctaEl, {
        duration: 0.25,
        scale: 1.5,
        backgroundColor: '#ffffff',
        color: '#c90010',
        ease: 'bounce.in', // rebote al iniciar
      })
      .to(ctaEl, {
        duration: 0.5,
        opacity: 1,
      })
      .to(ctaEl, {
        duration: 0.25,
        scale: 1,
        backgroundColor: '#c90010',
        color: '#ffffff',
        ease: 'bounce.out', // rebote al finalizar
      });

    // Disparo con GSAP ScrollTrigger cuando el CTA entra en viewport
    const st = ScrollTrigger.create({
      trigger: ctaEl,
      start: 'top 80%',
      end: 'bottom 20%',
      once: false,
      onEnter: () => tl.restart(),
      onEnterBack: () => tl.restart(),
      //markers: true, // descomenta para depurar
    });

    return () => {
      st.kill();
      tl.kill();
    };
  }, []);

  return (
    <>
      {renderFormGolf && <GolfFormLoader onClose={() => setRenderFormGolf(false)} />}

      <BackgroundGtiColor ref={sectionRef} id="formulario-module">
        <FormContainer id="formulario">
          <DesktopTitleGroup>
            <FirstText><InterroganteApertura>¿</InterroganteApertura>QUIERES CONDUCIR</FirstText>
            <SecondLineWrapper>
              <SecondTextWhite>UNA&nbsp;</SecondTextWhite>
              <SecondTextJoined>LEYENDA?</SecondTextJoined>
            </SecondLineWrapper>
          </DesktopTitleGroup>

          <MobileTitleGroup>
            <MobileTitleLine as={FirstText}><InterroganteApertura>¿</InterroganteApertura>QUIERES</MobileTitleLine>
            <MobileTitleLine as={FirstText}>CONDUCIR UNA</MobileTitleLine>
            <MobileTitleLine as={SecondTextJoined}>LEYENDA?</MobileTitleLine>
          </MobileTitleGroup>
          <Container
            horizontalAlign={'center'}
            wrap={'always'}
            padding={{ top: ContainerPadding.static500 }}
            gutter={ContainerGutter.static400}
          >
            <Container
              horizontalAlign={'center'}
              wrap={'always'}
              padding={{ left: ContainerPadding.dynamic0200, right: ContainerPadding.dynamic0200 }}
            >
              <DescriptionText appearance={TokenTextAppearance.headline200}>
                Golf GTI desde 39.900€, sujeto a financiación. <StyledReferenceBadge>1</StyledReferenceBadge>
              </DescriptionText>
              <SecondaryText appearance={TokenTextAppearance.headline200}>
                
              </SecondaryText>
            </Container>

            <StyledCTA
              ref={ctaRef as unknown as React.Ref<HTMLButtonElement>}
              tag="button"
              emphasis="primary"
              type="button"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate('/mas-informacion');
              }}
              aria-label="Pide más información del Golf GTI"
            >
              <Text appearance={TokenTextAppearance.headline100} bold>
                Pide más información{' '}
              </Text>
            </StyledCTA>
          </Container>
        </FormContainer>

        <ScrollHintArrowLayer>
          <ScrollHintArrow trigger={sectionRef} targetSelector="#timeline-gti-module" />
        </ScrollHintArrowLayer>
      </BackgroundGtiColor>
    </>
  );
};
